import { GLOBAL_EVENTS, VIEW } from '../enums';
import { StateUpdater, useEffect } from 'preact/hooks';
import { Theme, generateTheme } from '../styles';
import Usercentrics, { InitialUIValues, TRACKING_EVENT_TYPE, UI_LAYER } from '@usercentrics/cmp-browser-sdk';
import { getAmpClientConfig, isEqual, setTrackingPixel } from '../modules/helpers';
import { RTL_LANGUAGES } from '../constants';
import { useBodyScrolling } from './useBodyScrolling';
import { useCategoryContext } from '../contexts/CategoryContext';
import { useSettingsContext } from '../contexts/SettingsContext';
import { useTheme } from 'styled-components';
import { useUiContext } from '../contexts/UiContext';

export const useInitApp = (
  UC: Usercentrics,
  initialUIValues: InitialUIValues,
  onThemeChange: StateUpdater<Theme>,
  onAppInitialized: () => void,
): void => {
  const { setCategories } = useCategoryContext();

  const { settings, setSettings } = useSettingsContext();
  const { setBaseHref, setIsAmpEnabled, setUiVariant, setView, view, previousView } = useUiContext();
  const { setBodyScrolling } = useBodyScrolling();
  const theme = useTheme();

  useEffect(() => {
    const newSettings = UC.getSettings();
    setSettings(newSettings);
    setCategories(UC.getCategories());
    setUiVariant(initialUIValues.variant);
    setIsAmpEnabled(initialUIValues.ampEnabled);

    if (initialUIValues.ampEnabled) {
      const clientConfig = getAmpClientConfig();
      setBaseHref(clientConfig?.baseHref || '');
    }

    // The SDK tells the UI which layer should be displayed to the user, depending on the data in the localstorage and
    // the customer's admin interface choices.
    switch (initialUIValues.initialLayer) {
      case UI_LAYER.FIRST_LAYER:
        setView(VIEW.FIRST_LAYER);
        setBodyScrolling(false);
        setTrackingPixel(TRACKING_EVENT_TYPE.CMP_SHOWN, UC.setTrackingPixel);
        break;
      case UI_LAYER.PRIVACY_BUTTON:
        setView(VIEW.PRIVACY_BUTTON);
        setBodyScrolling(true);
        break;
      case UI_LAYER.SECOND_LAYER:
        setView(VIEW.SECOND_LAYER);
        setBodyScrolling(false);
        setTrackingPixel(TRACKING_EVENT_TYPE.CMP_SHOWN, UC.setTrackingPixel);
        break;
      default:
        setView(VIEW.NONE);
        setBodyScrolling(true);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const newSettings = UC.getSettings();

    // We generate a new custom LightTheme with the font-size, font-family, and primary color from the settings.
    const customTheme = generateTheme(
      newSettings.ui,
      RTL_LANGUAGES.includes(newSettings.ui.language.selected),
      initialUIValues.variant,
    );

    // If we skip this equal check, we cause an endless loop of re-renders, because preact will always trigger a
    // rerender on theme change.
    if (!isEqual(customTheme, theme)) {
      // Updates the theme state on a parent component
      onThemeChange(customTheme);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    onAppInitialized();
    window.dispatchEvent(new window.CustomEvent(GLOBAL_EVENTS.INITIALIZED));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (view !== previousView) {
      const viewChangeEvent = new window.CustomEvent(GLOBAL_EVENTS.VIEW_CHANGED, { detail: { previousView, view } });
      window.dispatchEvent(viewChangeEvent);
    }
  }, [previousView, view]);

  useEffect(() => {
    if (!settings.ui) {
      return;
    }

    if (view !== previousView) {
      switch (view) {
        case VIEW.FIRST_LAYER: {
          const { isOverlayEnabled } = settings.ui.firstLayer;

          if (isOverlayEnabled) {
            setBodyScrolling(false);
          } else {
            setBodyScrolling(true);
          }
          break;
        }
        case VIEW.SECOND_LAYER: {
          const { isOverlayEnabled } = settings.ui.secondLayer;

          if (isOverlayEnabled) {
            setBodyScrolling(false);
          } else {
            setBodyScrolling(true);
          }
          break;
        }
        case VIEW.PRIVACY_BUTTON:
        case VIEW.NONE:
        default: {
          setBodyScrolling(true);
        }
      }
    }
  }, [previousView, setBodyScrolling, settings.ui, view]);
};
